import { apiClient } from "./client";

export interface AdminStats {
  total_suppliers: number;
  pending_suppliers: number;
  approved_suppliers: number;
  rejected_suppliers: number;
  total_users: number;
  approval_rate: number;
}

export interface AdminUser {
  id: number;
  // Parallel UUID exposed by the backend so admin pages can call
  // /api/v1/v2/users/{uuid}/(de)activate without an extra lookup.
  uuid_id?: string | null;
  email: string;
  phone: string | null;
  role: string;
  is_active: boolean;
  is_verified: boolean;
  created_at: string | null;
  first_name: string | null;
  last_name: string | null;
  city: string | null;
  company_name: string | null;
}

export interface AdminUsersResponse {
  total: number;
  skip: number;
  limit: number;
  items: AdminUser[];
}

export interface AdminUsersParams {
  role?: string;
  search?: string;
  is_active?: boolean;
  is_verified?: boolean;
  skip?: number;
  limit?: number;
}

export interface OrganizerVerificationRequest {
  id: number;
  user_id: number;
  document_path: string | null;
  status: string;
  admin_comment: string | null;
  reviewed_at: string | null;
  created_at: string | null;
  organizer_email: string;
  organizer_company: string | null;
}

export interface SupplierVerificationRequest {
  id: number;
  user_id: number;
  document_path: string | null;
  status: string;
  admin_comment: string | null;
  reviewed_at: string | null;
  created_at: string | null;
  supplier_email: string;
}

export interface Moderator {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  is_active: boolean;
  is_verified: boolean;
  created_at: string | null;
}

export interface ModeratorCreatePayload {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

export interface DashboardMetrics {
  active_now: number;
  peak: number;
  avg_daily_users_30d: number;
  daily_unique_30d: { date: string; users: number }[];
  hour_load_avg_30d: { hour: number; avg_requests: number }[];
  monthly_unique_12m: { month: string; users: number }[];
}

export const AdminApi = {
  getStats: () => apiClient.get<AdminStats>("/admin/stats"),
  getDashboardMetrics: () =>
    apiClient.get<DashboardMetrics>("/admin/dashboard/metrics"),

  listUsers: (params?: AdminUsersParams) =>
    apiClient.get<AdminUsersResponse>("/admin/users", { params }),

  listModerators: () => apiClient.get<Moderator[]>("/admin/moderators"),
  createModerator: (payload: ModeratorCreatePayload) =>
    apiClient.post<Moderator>("/admin/moderators", payload),
  deactivateModerator: (id: number) => apiClient.delete(`/admin/moderators/${id}`),

  listOrganizerVerificationRequests: (params?: { status_filter?: string }) =>
    apiClient.get<OrganizerVerificationRequest[]>("/admin/organizer-verification-requests", { params }),
  approveOrganizerVerification: (id: number) =>
    apiClient.post(`/admin/organizer-verification-requests/${id}/approve`),
  rejectOrganizerVerification: (id: number, comment?: string) =>
    apiClient.post(`/admin/organizer-verification-requests/${id}/reject`, { comment }),

  listSupplierVerificationRequests: (params?: { status_filter?: string }) =>
    apiClient.get<SupplierVerificationRequest[]>("/admin/supplier-verification-requests", { params }),
  approveSupplierVerification: (id: number) =>
    apiClient.post(`/admin/supplier-verification-requests/${id}/approve`),
  rejectSupplierVerification: (id: number, comment?: string) =>
    apiClient.post(`/admin/supplier-verification-requests/${id}/reject`, { comment }),
};
